<ng-container *ngIf="!loading; else isLoading">
  <div class="column--2 align-right">
    <form [formGroup]="form">
      <mat-form-field class="w-100">
        <mat-label>Couriers</mat-label>
        <mat-select formControlName="couriers" name="couriers" placeholder="All couriers" multiple>
          <mat-option *ngFor="let option of couriersDropdown" value="{{ option.id }}">{{ option.text }}</mat-option>
        </mat-select>
      </mat-form-field>
    </form>
  </div>

  <div class="row mb--md">
    <div class="column--12">
      <mat-card class="summary-card">
        <mat-card-content class="card p--md">
          <mat-card-title>PERFORMANCE SUMMARY</mat-card-title>
          <div class="row summary-content">
            <div class="column--3">
              <p>Total Shipments</p>
              <h2>{{ totalShipments | number }}</h2>
            </div>
            <div class="column--3">
              <p>On-Time Rate</p>
              <h2>{{ onTimeRate | number : '1.2-2' }}%</h2>
            </div>
            <div class="column--3">
              <p>Average Delay</p>
              <h2>{{ averageDelay | number : '1.2-2' }}h</h2>
            </div>
            <!-- Claims metrics (always show if available) -->
            <div class="column--3" *ngIf="hasClaimsData">
              <p>Claim Rate</p>
              <h2 [ngStyle]="{ color: getClaimRateColor(overallClaimRate) }">
                {{ formatClaimRate(overallClaimRate) }}
                <mat-icon class="trend-icon small" [ngClass]="getClaimsTrendClass(claimRateTrend)">
                  {{ getTrendIcon(claimRateTrend) }}
                </mat-icon>
              </h2>
            </div>
          </div>
          <div class="row" *ngIf="hasClaimsData">
            <div class="column--3">
              <p>Total Claims</p>
              <h2>{{ totalClaims | number }}</h2>
            </div>
            <div class="column--9">
              <!-- Additional claims metrics can go here if needed -->
            </div>
          </div>
          <div class="row" *ngIf="!hasClaimsData">
            <div class="column--12">
              <p class="no-claims-message">No claims data available for this time period.</p>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <div class="row mb--md">
    <div class="column--6">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>GRADE DISTRIBUTION</mat-card-title>
          <div class="chart-container">
            <ngx-charts-pie-chart [results]="gradeDistribution" [legend]="true" [labels]="true"> </ngx-charts-pie-chart>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
    <div class="column--6">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>GRADE DISTRIBUTION TRENDS</mat-card-title>
          <div class="chart-container">
            <ngx-charts-area-chart-stacked
              [results]="gradeDistributionTrends"
              [gradient]="true"
              [xAxis]="true"
              [yAxis]="true"
              [legend]="true"
              [showXAxisLabel]="true"
              [showYAxisLabel]="true"
              xAxisLabel="Time Period"
              yAxisLabel="Shipment Count"
              [scheme]="performanceColorScheme"
              [yAxisTickFormatting]="formatYAxisNumberTicks"
            >
            </ngx-charts-area-chart-stacked>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Comparison across couriers -->
  <mat-card class="chart-card">
    <mat-card-header>
      <mat-card-title>COURIER DELAY DISTRIBUTION</mat-card-title>
      <div class="title-spacer"></div>
      <div class="view-toggle">
        <mat-button-toggle-group [(ngModel)]="showAsPercentage" (change)="onViewToggleChange($event.value)">
          <mat-button-toggle [value]="false">Count</mat-button-toggle>
          <mat-button-toggle [value]="true">Percentage</mat-button-toggle>
        </mat-button-toggle-group>
      </div>
    </mat-card-header>
    <mat-card-content>
      <div class="chart-container">
        <!-- Count view -->
        <ngx-charts-bar-horizontal-stacked
          *ngIf="!showAsPercentage"
          [results]="courierDelayDistribution"
          [gradient]="false"
          [xAxis]="true"
          [yAxis]="true"
          [legend]="true"
          [showXAxisLabel]="true"
          [showYAxisLabel]="true"
          xAxisLabel="Number of Shipments"
          yAxisLabel="Courier"
          [scheme]="delayColorScheme"
          [showGridLines]="true"
        >
          <ng-template #tooltipTemplate let-model="model">
            <div class="tooltip-content">
              <div>
                <strong>{{ model.series }}: {{ model.value | number }}</strong>
              </div>
              <div>Courier: {{ model.name }}</div>
            </div>
          </ng-template>
        </ngx-charts-bar-horizontal-stacked>

        <!-- Percentage view -->
        <ngx-charts-bar-horizontal-normalized
          *ngIf="showAsPercentage"
          [results]="courierDelayPercentageData"
          [gradient]="false"
          [xAxis]="true"
          [yAxis]="true"
          [legend]="true"
          [showXAxisLabel]="true"
          [showYAxisLabel]="true"
          xAxisLabel="Percentage of Shipments"
          yAxisLabel="Courier"
          [scheme]="delayColorScheme"
          [showGridLines]="true"
        >
          <ng-template #tooltipTemplate let-model="model">
            <div class="tooltip-content">
              <div>
                <strong>{{ model.series }}: {{ model.value | number : '1.1-1' }}%</strong>
              </div>
              <div>Courier: {{ model.name }}</div>
            </div>
          </ng-template>
        </ngx-charts-bar-horizontal-normalized>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Trend analysis for selected courier -->
  <mat-card class="chart-card" *ngIf="selectedCourier">
    <mat-card-header>
      <mat-card-title>Delay Trend for {{ selectedCourier }}</mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <div class="chart-container">
        <ngx-charts-line-chart
          [results]="selectedCourierTrendData"
          [gradient]="false"
          [xAxis]="true"
          [yAxis]="true"
          [legend]="true"
          [showXAxisLabel]="true"
          [showYAxisLabel]="true"
          xAxisLabel="Time Period"
          yAxisLabel="Number of Shipments"
          [scheme]="delayColorScheme"
          [showGridLines]="true"
        >
        </ngx-charts-line-chart>
      </div>
    </mat-card-content>
  </mat-card>

  <div class="row mb--md">
    <!-- Service Breakdown Card -->
    <div class="column--6">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>SERVICE BREAKDOWN</mat-card-title>
          <div class="service-summary">
            <div class="summary-row">
              <div class="label">Total Services:</div>
              <div class="value">{{ allServiceMetrics.length | number }}</div>
            </div>
            <div class="summary-row" *ngIf="allServiceMetrics.length > 0">
              <div class="label">Top Service:</div>
              <div class="value">{{ allServiceMetrics[0]?.name || 'N/A' }}</div>
            </div>
            <div class="summary-row" *ngIf="allServiceMetrics.length > 0">
              <div class="label">Top Service Volume:</div>
              <div class="value">{{ allServiceMetrics[0]?.totalShipments | number }}</div>
            </div>
          </div>
          <div class="text-center mt--lg">
            <button mat-raised-button color="primary" (click)="navigateToServicesView()">
              <mat-icon>analytics</mat-icon> View Detailed Service Breakdown
            </button>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <div class="column--6">
      <!-- Claims by Grade Card -->
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>CLAIMS BY GRADE</mat-card-title>
          <div class="chart-container" *ngIf="hasClaimsData && claimsByGradeData.length > 0">
            <ngx-charts-pie-chart
              [results]="claimsByGradeData"
              [legend]="true"
              [labels]="true"
              [scheme]="claimsColorScheme"
            >
            </ngx-charts-pie-chart>
          </div>
          <div class="no-data-message" *ngIf="!hasClaimsData || claimsByGradeData.length === 0">
            <p>No claims data available for this time period.</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <div class="row mb--md">
    <div class="column--12">
      <!-- Guaranteed Service Metrics -->
      <mat-card class="summary-card">
        <mat-card-content class="card p--md">
          <mat-card-title>GUARANTEED SERVICE METRICS</mat-card-title>
          <div class="row summary-content">
            <div class="column--3">
              <p>Total Shipments</p>
              <h2>{{ gTotalShipments | number }}</h2>
            </div>
            <div class="column--3">
              <p>Success Rate</p>
              <h2 class="text-primary-green">{{ gOnTimeRate | number : '1.2-2' }}%</h2>
            </div>
            <div class="column--3">
              <p>On-Time Count</p>
              <h2>{{ gOnTimeCount | number }}</h2>
            </div>
            <div class="column--3">
              <p>Average Delay</p>
              <h2>{{ gAverageDelay | number : '1.2-2' }}h</h2>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <div class="row mb--md">
    <div class="column--12">
      <!-- Courier Comparison Card -->
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>COURIER VOLUME BREAKDOWN</mat-card-title>
          <div class="chart-container">
            <ngx-charts-bar-horizontal
              [results]="courierComparison"
              [xAxis]="true"
              [yAxis]="true"
              [scheme]="defaultColorScheme"
            >
            </ngx-charts-bar-horizontal>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Always show Courier Claim Rates chart if claims data is available -->
  <div class="row mb--md" *ngIf="hasClaimsData && courierClaimRates.length > 0">
    <div class="column--12">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>COURIER CLAIM RATES</mat-card-title>
          <div class="chart-container">
            <ngx-charts-bar-horizontal
              [results]="courierClaimRates"
              [xAxis]="true"
              [yAxis]="true"
              [scheme]="claimsColorScheme"
            >
            </ngx-charts-bar-horizontal>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Always show Claims Trend Analysis if claims data is available -->
  <div class="row mb--md" *ngIf="hasClaimsData">
    <div class="column--12">
      <mat-card class="chart-card">
        <mat-card-content class="card p--md">
          <mat-card-title>CLAIMS TREND ANALYSIS</mat-card-title>
          <div class="chart-container">
            <!-- Claims rate over time -->
            <ngx-charts-line-chart
              [results]="
                courierClaimsData.length > 0 ? courierClaimsData : [{ name: 'Overall', series: claimTrendData }]
              "
              [gradient]="true"
              [xAxis]="true"
              [yAxis]="true"
              [legend]="true"
              [showXAxisLabel]="true"
              [showYAxisLabel]="true"
              xAxisLabel="Time Period"
              yAxisLabel="Claim Rate (%)"
              [scheme]="claimsColorScheme"
            >
            </ngx-charts-line-chart>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Keep the rest of the page (detailed metrics, shipments) the same -->
  <div class="row mb--md">
    <div class="column--12">
      <!-- Detailed Metrics Table - updated to always show claims columns if data is available -->
      <mat-card class="table-card">
        <mat-card-content class="card p--md">
          <mat-card-title>DETAILED METRICS</mat-card-title>
          <div class="responsive-table">
            <table mat-table [dataSource]="sortedData" matSort (matSortChange)="sortData($event)">
              <!-- Courier Column -->
              <ng-container matColumnDef="courier">
                <th mat-header-cell *matHeaderCellDef>COURIER</th>
                <td mat-cell *matCellDef="let element">{{ element.courier }}</td>
              </ng-container>

              <!-- Total Column -->
              <ng-container matColumnDef="total">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>TOTAL</th>
                <td mat-cell *matCellDef="let element">{{ element.total | number }}</td>
              </ng-container>

              <!-- On-Time Column -->
              <ng-container matColumnDef="onTime">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>ON-TIME</th>
                <td mat-cell *matCellDef="let element">{{ element.onTime | number : '1.2-2' }}</td>
              </ng-container>

              <!-- On-Time % Column -->
              <ng-container matColumnDef="onTimePercent">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>ON-TIME %</th>
                <td mat-cell *matCellDef="let element">{{ element.onTimePercent | number : '1.2-2' }}%</td>
              </ng-container>

              <!-- Avg Delay Column -->
              <ng-container matColumnDef="avgDelay">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>AVG DELAY</th>
                <td mat-cell *matCellDef="let element">{{ element.avgDelay | number : '1.2-2' }}</td>
              </ng-container>

              <!-- Total Claims Column (only in display when hasClaimsData) -->
              <ng-container matColumnDef="claims">
                <th mat-header-cell *matHeaderCellDef>CLAIMS</th>
                <td mat-cell *matCellDef="let element">
                  {{ element.totalClaims !== undefined ? (element.totalClaims | number) : 'N/A' }}
                </td>
              </ng-container>

              <!-- Claim Rate Column (only in display when hasClaimsData) -->
              <ng-container matColumnDef="claimRate">
                <th mat-header-cell *matHeaderCellDef>CLAIM RATE</th>
                <td mat-cell *matCellDef="let element" [ngStyle]="{ color: getClaimRateColor(element.claimRate) }">
                  {{ element.claimRate !== undefined ? (element.claimRate | number : '1.2-2') + '%' : 'N/A' }}
                </td>
              </ng-container>

              <!-- Late Delivery Claims Column (only in display when hasClaimsData) -->
              <ng-container matColumnDef="lateDeliveryClaims">
                <th mat-header-cell *matHeaderCellDef>LATE DELIVERY CLAIMS</th>
                <td mat-cell *matCellDef="let element">
                  {{ element.lateDeliveryClaims !== undefined ? (element.lateDeliveryClaims | number) : 'N/A' }}
                  <span *ngIf="element.lateDeliveryClaimRate !== undefined">
                    ({{ element.lateDeliveryClaimRate | number : '1.2-2' }}%)
                  </span>
                </td>
              </ng-container>

              <!-- Grade A % Column -->
              <ng-container matColumnDef="gradeA">
                <th mat-header-cell *matHeaderCellDef>GRADE A %</th>
                <td mat-cell *matCellDef="let element">{{ element.gradeA | percent : '1.2-2' }}</td>
              </ng-container>

              <!-- Grade B % Column -->
              <ng-container matColumnDef="gradeB">
                <th mat-header-cell *matHeaderCellDef>GRADE B %</th>
                <td mat-cell *matCellDef="let element">{{ element.gradeB | percent : '1.2-2' }}</td>
              </ng-container>

              <!-- Grade C % Column -->
              <ng-container matColumnDef="gradeC">
                <th mat-header-cell *matHeaderCellDef>GRADE C %</th>
                <td mat-cell *matCellDef="let element">{{ element.gradeC | percent : '1.2-2' }}</td>
              </ng-container>

              <!-- Grade D % Column -->
              <ng-container matColumnDef="gradeD">
                <th mat-header-cell *matHeaderCellDef>GRADE D %</th>
                <td mat-cell *matCellDef="let element">{{ element.gradeD | percent : '1.2-2' }}</td>
              </ng-container>

              <!-- Grade F % Column -->
              <ng-container matColumnDef="gradeF">
                <th mat-header-cell *matHeaderCellDef>GRADE F %</th>
                <td mat-cell *matCellDef="let element">{{ element.gradeF | percent : '1.2-2' }}</td>
              </ng-container>

              <!-- G-Service Column -->
              <ng-container matColumnDef="gService">
                <th mat-header-cell *matHeaderCellDef>G-SERVICE</th>
                <td mat-cell *matCellDef="let element">{{ element.gService | number : '1.2-2' }}%</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
            </table>
          </div>
          <mat-paginator
            (page)="pageChanged($event)"
            [length]="sortedData.length"
            [pageSize]="itemsPerPage"
            [pageSizeOptions]="itemsPerPageOptions"
            [pageIndex]="currentPage"
            [disabled]="sortedData.length <= itemsPerPage"
          >
          </mat-paginator>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <div class="row mb--md">
    <div class="column--12">
      <mat-card class="table-card">
        <mat-card-content class="card p--md">
          <div class="row mb--md">
            <div class="column--6">
              <mat-card-title>SHIPMENTS</mat-card-title>
            </div>
            <div class="column--6 text-right">
              <button
                mat-raised-button
                color="primary"
                (click)="exportCourierShipments()"
                [disabled]="isExporting || totalShipmentsCount === 0"
              >
                <mat-icon>file_download</mat-icon>
                <span *ngIf="!isExporting">Export to CSV</span>
                <span *ngIf="isExporting">Exporting... ({{ exportProgress }}%)</span>
              </button>
            </div>
          </div>

          <div *ngIf="exportDownloadUrl" class="row mb--md">
            <div class="column--12">
              <div class="export-success-banner">
                <mat-icon>check_circle</mat-icon>
                <span>Export completed! </span>
                <a [href]="exportDownloadUrl" target="_blank">Download CSV</a>
              </div>
            </div>
          </div>

          <div class="responsive-table">
            <table mat-table [dataSource]="courierShipments">
              <!-- Account Type Column -->
              <ng-container matColumnDef="accountType">
                <th mat-header-cell *matHeaderCellDef>ACCOUNT TYPE</th>
                <td mat-cell *matCellDef="let element">{{ element.account_type || 'N/A' }}</td>
              </ng-container>

              <!-- Courier Column -->
              <ng-container matColumnDef="courier">
                <th mat-header-cell *matHeaderCellDef>COURIER</th>
                <td mat-cell *matCellDef="let element">{{ element.courier_name }}</td>
              </ng-container>

              <!-- Tracking Number Column -->
              <ng-container matColumnDef="trackingNumber">
                <th mat-header-cell *matHeaderCellDef>TRACKING #</th>
                <td mat-cell *matCellDef="let element">{{ element.tracking_number }}</td>
              </ng-container>

              <!-- Has Claim Column -->
              <ng-container matColumnDef="hasClaim">
                <th mat-header-cell *matHeaderCellDef>CLAIM</th>
                <td mat-cell *matCellDef="let element">
                  <div class="claim-status">
                    <mat-icon *ngIf="element.has_claim" class="claims-icon warning">report_problem</mat-icon>
                    <span *ngIf="!element.has_claim">No</span>
                    <span *ngIf="element.has_claim && element.claim_status">{{ element.claim_status }}</span>
                    <span *ngIf="element.has_claim && !element.claim_status">Yes</span>
                  </div>
                </td>
              </ng-container>

              <!-- Shipment Created At Column -->
              <ng-container matColumnDef="shipmentCreatedAt">
                <th mat-header-cell *matHeaderCellDef>CREATED AT</th>
                <td mat-cell *matCellDef="let element">{{ element.shipment_created_at | date : 'short' }}</td>
              </ng-container>

              <!-- Estimated Delivery Column -->
              <ng-container matColumnDef="estimatedDelivery">
                <th mat-header-cell *matHeaderCellDef>ESTIMATED DELIVERY</th>
                <td mat-cell *matCellDef="let element">{{ element.estimated_delivery | date : 'short' }}</td>
              </ng-container>

              <!-- Pickup Time Column -->
              <ng-container matColumnDef="pickupTime">
                <th mat-header-cell *matHeaderCellDef>PICKUP TIME</th>
                <td mat-cell *matCellDef="let element">{{ element.pickup_time | date : 'short' }}</td>
              </ng-container>

              <!-- Expected Delivery Column -->
              <ng-container matColumnDef="expectedDelivery">
                <th mat-header-cell *matHeaderCellDef>EXPECTED DELIVERY</th>
                <td mat-cell *matCellDef="let element">{{ element.expected_delivery | date : 'short' }}</td>
              </ng-container>

              <!-- Actual Delivery Column -->
              <ng-container matColumnDef="actualDelivery">
                <th mat-header-cell *matHeaderCellDef>ACTUAL DELIVERY</th>
                <td mat-cell *matCellDef="let element">{{ element.actual_delivery | date : 'short' }}</td>
              </ng-container>

              <!-- Origin City Column -->
              <ng-container matColumnDef="originCity">
                <th mat-header-cell *matHeaderCellDef>ORIGIN CITY</th>
                <td mat-cell *matCellDef="let element">{{ element.origin_city }}</td>
              </ng-container>

              <!-- Origin Province Column -->
              <ng-container matColumnDef="originProvince">
                <th mat-header-cell *matHeaderCellDef>ORIGIN PROVINCE</th>
                <td mat-cell *matCellDef="let element">{{ element.origin_province }}</td>
              </ng-container>

              <!-- Destination City Column -->
              <ng-container matColumnDef="destinationCity">
                <th mat-header-cell *matHeaderCellDef>DESTINATION CITY</th>
                <td mat-cell *matCellDef="let element">{{ element.destination_city }}</td>
              </ng-container>

              <!-- Destination Province Column -->
              <ng-container matColumnDef="destinationProvince">
                <th mat-header-cell *matHeaderCellDef>DESTINATION PROVINCE</th>
                <td mat-cell *matCellDef="let element">{{ element.destination_province }}</td>
              </ng-container>

              <!-- Performance Grade Column -->
              <ng-container matColumnDef="performanceGrade">
                <th mat-header-cell *matHeaderCellDef>GRADE</th>
                <td mat-cell *matCellDef="let element">{{ element.performance_grade || 'N/A' }}</td>
              </ng-container>

              <!-- Service Name Column -->
              <ng-container matColumnDef="serviceName">
                <th mat-header-cell *matHeaderCellDef>SERVICE</th>
                <td mat-cell *matCellDef="let element">{{ element.service_name }}</td>
              </ng-container>

              <!-- Is Guaranteed Column -->
              <ng-container matColumnDef="isGuaranteed">
                <th mat-header-cell *matHeaderCellDef>GUARANTEED</th>
                <td mat-cell *matCellDef="let element">{{ element.is_guaranteed ? 'Yes' : 'No' }}</td>
              </ng-container>

              <!-- Delay Column -->
              <ng-container matColumnDef="delay">
                <th mat-header-cell *matHeaderCellDef>DELAY (HOURS)</th>
                <td mat-cell *matCellDef="let element">{{ element.delay | number : '1.2-2' }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="shipmentColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: shipmentColumns"
                [ngClass]="{ 'has-claim-row': row.has_claim }"
              ></tr>
            </table>
          </div>
          <mat-paginator
            (page)="shipmentPageChanged($event)"
            [length]="totalShipmentsCount"
            [pageSize]="shipmentsPerPage"
            [pageSizeOptions]="itemsPerPageOptions"
            [pageIndex]="currentShipmentsPage"
          >
          </mat-paginator>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</ng-container>
<ng-template #isLoading>
  <div class="align-center mt--lg">
    <app-loading></app-loading>
  </div>
</ng-template>
